import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import type { JSX } from 'react';
import { bookPreviewApi, millisUntilSignedUrlRefresh } from '../api/book-preview.js';
import { booksApi } from '../api/books.js';
import { BookReader } from '../books/BookReader.js';
import { useI18n } from '../i18n/context.js';
import { useAuth } from '../auth/context.js';

export function BookPreviewPage(): JSX.Element {
  const { id = '', ownerId } = useParams();
  const { user } = useAuth();
  const { t } = useI18n();
  const adminMode = Boolean(ownerId);
  const canAccess = !adminMode || user?.role === 'admin';
  const builderPath = adminMode ? `/admin/accounts/${ownerId}/books/${id}` : `/app/books/${id}`;
  // The same query the builder reads: whether the book is paid decides the bar's call to action.
  const book = useQuery({
    queryKey: ['book', id],
    queryFn: () => booksApi.get(id),
    enabled: Boolean(id) && canAccess && !adminMode,
  });
  const unpaid = !adminMode && book.data !== undefined && !book.data.paidAt;
  const query = useQuery({
    queryKey: ['book-preview', id],
    queryFn: () => bookPreviewApi.get(id),
    enabled: Boolean(id) && canAccess,
    /**
     * Подписанные media-ссылки живут час, а чтение может длиться дольше. Обновляем их
     * заранее по сроку из ответа и вдобавок при возврате на вкладку: глобально
     * `refetchOnWindowFocus` выключен, и вернувшийся через полчаса читатель
     * иначе увидит пустые страницы.
     */
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: true,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      return millisUntilSignedUrlRefresh(data) ?? false;
    },
  });

  if (!canAccess) {
    return (
      <main className="grid min-h-screen place-items-center">
        <p role="alert">{t.admin.forbidden}</p>
      </main>
    );
  }

  if (query.isPending) {
    return (
      <main className="grid min-h-screen place-items-center">
        <p role="status">{t.common.loading}</p>
      </main>
    );
  }

  if (!query.data) {
    return (
      <main className="grid min-h-screen place-items-center p-6 text-center">
        <div>
          <p role="alert">{t.preview.loadError}</p>
          <Link to={builderPath} className="kitup-secondary-button mt-4">
            {t.preview.backToBuilder}
          </Link>
        </div>
      </main>
    );
  }

  return (
    <>
      <header className="kitup-public-bar">
        <Link to={builderPath} className="kitup-secondary-button">
          ← {t.preview.backToBuilder}
        </Link>
        {/*
          Before payment the preview ends after the free spread and looked like a finished
          book. The bar says there is more and where to get it.
        */}
        {unpaid ? (
          <Link to={`${builderPath}?step=illustrations`} className="kitup-primary-button">
            {t.preview.unlockRest}
          </Link>
        ) : null}
      </header>
      <main className="kitup-preview-page kitup-preview-page-public">
        <BookReader book={query.data} />
      </main>
    </>
  );
}
